import {FiltersUpdateSource} from '@/components/features/search/filters/FiltersUtils/types';
import useFilters from '@/components/features/search/filters/FiltersUtils/useFilters';
import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import {useEventBarV2} from '../EventBarV2Provider';
import {type StagedSearchFilter} from '../utils/what';

// Overrides let a caller submit an explicit value in the same tick it selects one.
export type SubmitSearchOverride = {keyword?: string; stagedFilter?: StagedSearchFilter | null};

// Reuses v1's keyword path: updateFilters navigates app-wide (shallow refetch on the search
// page, else router.push to the search results). Dietary options ride along as a filter.
export const useEventBarV2Search = () => {
  const {updateFilters} = useFilters();
  const {whatValue, dietaryOptions, stagedSearchFilter, setPendingSearchSubmissions} =
    useEventBarV2();
  const {isOnSearchPage} = useStoreNextRouter();

  // `fulfillmentDetailIdOverride` points the search at a specific detail instead of the current
  // context one — used to re-run a past search under the history entry's own detail, or to search a
  // freshly created detail on a no-detail page. Resolves once the navigation lands.
  const submitSearch = async (
    override?: SubmitSearchOverride,
    fulfillmentDetailIdOverride?: string,
  ): Promise<void> => {
    // A suggested-search chip stages a structured filter instead of a keyword; apply it (clearing
    // any keyword so it's a filter-only search) with the chip's own tracking source.
    const filter =
      override && 'stagedFilter' in override ? override.stagedFilter : stagedSearchFilter;
    if (filter) {
      await updateFilters(
        {...filter.filters, keyword: '', dietaryOptions},
        filter.source,
        false,
        fulfillmentDetailIdOverride,
      );
      return;
    }

    await updateFilters(
      {
        keyword: (override?.keyword ?? whatValue).replace(/\s+/g, ' ').trim(),
        dietaryOptions,
      },
      FiltersUpdateSource.Keyword,
      false,
      fulfillmentDetailIdOverride,
    );
  };

  // Runs a commit-then-search sequence as a single search. On the search page, committing the
  // staged fields updates the fulfillment detail in the Apollo cache before the filter push lands,
  // and each would refetch the results on its own; holding `isSubmittingSearch` for the whole run
  // lets the results query wait for both. Elsewhere the search navigates to a fresh page, so there's
  // nothing to hold. The results show as loading for the whole run, so wrap only the commit and the
  // search — validate first, outside it, since a search that bails out would just flash the loading.
  const runSearchSubmission = async (submit: () => Promise<void>): Promise<void> => {
    if (!isOnSearchPage) return submit();
    setPendingSearchSubmissions(count => count + 1);
    try {
      await submit();
    } finally {
      setPendingSearchSubmissions(count => count - 1);
    }
  };

  return {submitSearch, runSearchSubmission};
};
